/*
 * The writing endpoint is conversational: asked for "a few options" it answers
 * with a preamble and a numbered list, not a bare replacement. Splicing that
 * into an email verbatim is how "Replace selection" ended up inserting
 * "Certainly! Here are some alternative versions:" into the body.
 *
 * So parse the shape before offering to apply it: one option means one
 * replacement, several mean the user picks -- or turns them into spintax.
 */

const PREAMBLE = /^(certainly|sure|of course|absolutely|here (are|is)|here's|below (are|is)|sure thing)\b/i;

const stripQuotes = value =>
  value
    .replace(/^\s*["“”']+/, "")
    .replace(/["“”']+\s*$/, "")
    .trim();

// Numbered ("1." / "1)") or bulleted ("-" / "•" / "*") list items.
const LIST_ITEM = /^\s*(?:\d+[.)]|[-•*])\s+(.*\S)\s*$/;

export const parseVariations = (raw = "") => {
  const lines = String(raw || "")
    .replace(/<br\s*\/?>/gi, "\n")
    .replace(/<\/(div|p)>/gi, "\n")
    .replace(/<[^>]+>/g, "")
    .split(/\r?\n/)
    .map(line => line.trim())
    .filter(Boolean);

  const options = [];
  let preamble = "";

  lines.forEach(line => {
    const match = line.match(LIST_ITEM);
    if (match) {
      const value = stripQuotes(match[1]);
      if (value) options.push(value);
      return;
    }
    // A trailing-colon lead-in, or an assistant pleasantry, belongs to the chat
    // rather than the email.
    if (!options.length && (PREAMBLE.test(line) || /:\s*$/.test(line))) {
      preamble = preamble || line;
      return;
    }
    if (!options.length) options.push(stripQuotes(line));
  });

  return {
    preamble,
    // A single item is an ordinary replacement; the caller need not treat it as
    // a choice.
    options: options.filter(Boolean)
  };
};

// Options are plain text but get pasted as HTML, so a stray < or & in the copy
// would be swallowed as markup.
export const escapeHtml = (value = "") =>
  String(value)
    .replace(/&/g, "&amp;")
    .replace(/</g, "&lt;")
    .replace(/>/g, "&gt;");

// {a|b|c} -- the editor turns this into spintax chips on paste.
export const toSpintax = (options = []) =>
  options.length > 1 ? `{${options.join("|")}}` : options[0] || "";

export default parseVariations;

// Merge tags are the one thing a rewrite must not quietly lose. A dropped
// {{firstName}} sends "Hi ," to everyone behind the step, and the model has no
// idea it happened -- so compare before offering to apply, not after.
const TOKEN = /\{\{\s*([^{}]+?)\s*\}\}/g;

const tokensIn = (value = "") => {
  const found = [];
  let match = TOKEN.exec(value);
  while (match) {
    const name = match[1].trim();
    if (name && !found.includes(name)) found.push(name);
    match = TOKEN.exec(value);
  }
  TOKEN.lastIndex = 0;
  return found;
};

export const tokenDiff = (before = "", after = "") => {
  const was = tokensIn(before);
  const now = tokensIn(after);
  return {
    removed: was.filter(name => !now.includes(name)),
    // Invented tags are the quieter failure: they render as raw text in the
    // sent email because they match no field.
    added: now.filter(name => !was.includes(name))
  };
};

// Not every reply is email copy. Asked something vague, the model answers with
// a clarifying question -- offering "Replace body" on that would put "Could you
// please clarify?" into the email.
const NOT_COPY = /^(it looks like|it seems|could you|can you|i'm not sure|i am not sure|sorry|i didn't|i did not|please clarify|what would you|happy to help|sure[,!.]? (what|which|could))/i;

export const looksLikeEmailCopy = (raw = "") => {
  const text = String(raw || "")
    .replace(/<[^>]+>/g, " ")
    .replace(/\s+/g, " ")
    .trim();
  if (!text) return false;
  if (NOT_COPY.test(text)) return false;
  // A single short line ending in a question mark is the model talking to you,
  // not writing to your prospect.
  if (text.length < 120 && /\?$/.test(text)) return false;
  return text.length >= 80;
};

// The writer sometimes leaves the subject inside the body -- "Subject: ..." or
// "**Subject Line:** ..." on the first line -- when constructContentText's
// stricter regex does not match. Applying that verbatim puts the subject line
// into the email body, so split it here before anything is offered.
const SUBJECT_LINE = /^\s*(?:\*\*|__)?\s*subject(?:\s*line)?\s*[:\-\u2013]\s*(?:\*\*|__)?\s*(.+?)\s*(?:\*\*|__)?\s*$/i;

export const splitSubject = (html = "") => {
  const text = String(html || "");
  // Work on the first block, whether the content is html or plain text.
  const match = text.match(
    /^\s*(?:<(?:div|p)[^>]*>)?\s*([^<\n]+?)\s*(?:<\/(?:div|p)>|\n|$)/i
  );
  const firstLine = match && match[1];
  const subjectMatch = firstLine && firstLine.match(SUBJECT_LINE);
  if (!subjectMatch) return { subject: "", body: text };
  return {
    subject: subjectMatch[1].replace(/^["\u201c']+|["\u201d']+$/g, "").trim(),
    body: text.slice(match[0].length).replace(/^(?:\s|<br\s*\/?>)+/i, "")
  };
};
